// Shared Messaging Service for cross-dashboard communication
import { supabase } from '../supabase/supabaseClient';
import authService from './AuthService';

class MessagingService {
  constructor() {
    this.messageSubscription = null;
    this.messageListeners = [];
  }

  // Initialize real-time subscriptions
  async init() {
    const user = authService.getCurrentUser();
    if (!user) return;

    // Subscribe to messages for current user
    this.messageSubscription = supabase
      .channel(`messages:${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `recipient_id=eq.${user.id}`
      }, (payload) => {
        this.handleNewMessage(payload.new);
      })
      .subscribe();
  }

  // Send message from SuperAdmin as Mendy to Patient
  async sendMendyMessage(patientId, message) {
    try {
      // Get admin user from localStorage
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        throw new Error('Not authenticated');
      }

      const user = JSON.parse(userStr);
      const adminId = user.userId || user.id;

      console.log('Sending Mendy message:', {
        adminId,
        patientId,
        messagePreview: message.substring(0, 50)
      });

      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: adminId,
          recipient_id: patientId,
          sender_type: 'mendy',
          message: message,
          created_at: new Date().toISOString(),
          read: false
        })
        .select()
        .single();

      if (error) {
        console.error('Error sending Mendy message:', error);
        throw error;
      }

      console.log('✅ Mendy message sent successfully:', data.id);
      return data;
    } catch (error) {
      console.error('Error in sendMendyMessage:', error);
      throw error;
    }
  }

  // Send message from Patient to Mendy (received by SuperAdmin)
  async replyToMendy(message) {
    try {
      // Get patient user from localStorage
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        throw new Error('Not authenticated');
      }

      const user = JSON.parse(userStr);
      const patientId = user.userId || user.patientId || user.id;

      console.log('Patient replying to Mendy:', {
        patientId,
        messagePreview: message.substring(0, 50)
      });

      // Find the SuperAdmin (in production, this would be more sophisticated)
      const { data: admins } = await supabase
        .from('user')
        .select('id')
        .eq('user_type', 'superadmin')
        .limit(1);

      if (!admins || admins.length === 0) {
        console.error('No SuperAdmin found');
        throw new Error('No SuperAdmin found');
      }

      const adminId = admins[0].id;

      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: patientId,
          recipient_id: adminId,
          sender_type: 'patient',
          message: message,
          created_at: new Date().toISOString(),
          read: false
        })
        .select()
        .single();

      if (error) {
        console.error('Error sending patient reply:', error);
        throw error;
      }

      console.log('✅ Patient reply sent successfully:', data.id);
      return data;
    } catch (error) {
      console.error('Error in replyToMendy:', error);
      throw error;
    }
  }

  // Get conversation history
  async getConversation(userId1, userId2) {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .or(`and(sender_id.eq.${userId1},recipient_id.eq.${userId2}),and(sender_id.eq.${userId2},recipient_id.eq.${userId1})`)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data;
  }

  // Get all conversations for SuperAdmin
  async getAllConversations() {
    if (!authService.hasPermission('superadmin')) {
      throw new Error('Insufficient permissions');
    }

    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        sender:user!sender_id(name, user_type),
        recipient:user!recipient_id(name, user_type)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Group by conversation
    const conversations = {};
    data.forEach(msg => {
      const key = msg.sender_type === 'patient' ? msg.sender_id : msg.recipient_id;
      if (!conversations[key]) {
        conversations[key] = [];
      }
      conversations[key].push(msg);
    });

    return conversations;
  }

  // Get conversation with a specific patient (for SuperAdmin)
  async getPatientConversation(patientId) {
    try {
      // Get admin user from localStorage
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        throw new Error('Not authenticated');
      }

      const user = JSON.parse(userStr);
      const adminId = user.userId || user.id;

      console.log('Loading conversation for patient:', patientId, 'Admin ID:', adminId);

      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_id.eq.${adminId},recipient_id.eq.${patientId}),and(sender_id.eq.${patientId},recipient_id.eq.${adminId})`)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error loading conversation:', error);
        throw error;
      }

      console.log(`Loaded ${data?.length || 0} messages for patient ${patientId}`);
      return data || [];
    } catch (error) {
      console.error('Error in getPatientConversation:', error);
      return [];
    }
  }

  // Get conversation for a patient (called from patient's ChatModal)
  async getPatientConversationForPatient(patientId) {
    try {
      console.log('Loading messages for patient (patient view):', patientId);

      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .or(`sender_id.eq.${patientId},recipient_id.eq.${patientId}`)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error loading patient conversation:', error);
        throw error;
      }

      console.log(`Patient view: Loaded ${data?.length || 0} messages`);
      return data || [];
    } catch (error) {
      console.error('Error in getPatientConversationForPatient:', error);
      return [];
    }
  }

  // Mark messages as read
  async markAsRead(messageIds) {
    const { error } = await supabase
      .from('messages')
      .update({ read: true })
      .in('id', messageIds);

    if (error) throw error;
  }

  // Get unread count
  async getUnreadCount(userId) {
    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('recipient_id', userId)
      .eq('read', false);

    if (error) throw error;
    return count;
  }

  // Handle new message
  handleNewMessage(message) {
    // Notify all listeners
    this.messageListeners.forEach(callback => callback(message));

    // Show browser notification if permitted
    if (Notification.permission === 'granted') {
      new Notification('New message from Mendy', {
        body: message.message.substring(0, 100),
        icon: '/logos/mendy_icon.png'
      });
    }
  }

  // Subscribe to new messages
  onNewMessage(callback) {
    this.messageListeners.push(callback);
    return () => {
      this.messageListeners = this.messageListeners.filter(cb => cb !== callback);
    };
  }

  // Cleanup
  cleanup() {
    if (this.messageSubscription) {
      supabase.removeChannel(this.messageSubscription);
    }
  }
}

// Export singleton
const messagingService = new MessagingService();
export default messagingService;